import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClients } from '../../hooks/useClients';
import { useAttorney } from '../../context/AttorneyContext';
import './Clients.css';

/**
 * Clients Component
 * Shows all clients and their applications
 */
function Clients() {
  const { t } = useTranslation('attorney');
  const { isInternal, isAttorneyAdmin } = useAttorney();
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');

  const { data, isLoading, error, refetch } = useClients({ assignmentFilter });

  const clients = data?.clients || [];
  const stats = data?.stats || {};

  // Filter clients by search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.applications?.some(app =>
        app.trademarkName?.toLowerCase().includes(search) ||
        app.applicationId?.toLowerCase().includes(search)
      )
    );
  });

  if (isLoading) {
    return (
      <div className="clients-loading">
        <div className="loading-spinner" />
        <p>{t('attorney.clients.loading', 'Loading clients...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clients-error">
        <p>{t('attorney.clients.error', 'Failed to load clients')}</p>
        <button onClick={() => refetch()}>
          {t('attorney.clients.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="clients-container">
      <div className="clients-header">
        <h1>{t('attorney.clients.title', 'My Clients')}</h1>
        <div className="clients-filters">
          {isInternal && (
            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value)}
              className="clients-filter-select"
            >
              <option value="all">
                {t('attorney.clients.allClients', 'All Clients')}
              </option>
              <option value="mine">
                {t('attorney.clients.assignedToMe', 'Assigned to Me')}
              </option>
              <option value="my_firm">
                {t('attorney.clients.myFirm', 'My Firm')}
              </option>
              {isAttorneyAdmin && (
                <option value="transferred">
                  {t('attorney.clients.transferred', 'Transferred to External')}
                </option>
              )}
            </select>
          )}
          <div className="clients-search">
            <input
              type="text"
              placeholder={t('attorney.clients.searchPlaceholder', 'Search clients, trademarks...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="clients-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.totalClients || 0}</span>
          <span className="stat-label">{t('attorney.clients.stats.totalClients', 'Total Clients')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.activeClients || 0}</span>
          <span className="stat-label">{t('attorney.clients.stats.activeClients', 'Active (30d)')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.totalApplications || 0}</span>
          <span className="stat-label">{t('attorney.clients.stats.totalApplications', 'Applications')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.totalPendingFilings || 0}</span>
          <span className="stat-label">{t('attorney.clients.stats.pendingFilings', 'Pending Filings')}</span>
        </div>
      </div>

      {/* Client List */}
      {filteredClients.length === 0 ? (
        <div className="clients-empty">
          <p>
            {searchTerm
              ? t('attorney.clients.noResults', 'No clients match your search')
              : t('attorney.clients.empty', 'No clients yet')}
          </p>
        </div>
      ) : (
        <div className="clients-list">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.userId}
              client={client}
              isExpanded={selectedClientId === client.userId}
              onToggle={() => setSelectedClientId(
                selectedClientId === client.userId ? null : client.userId
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ client, isExpanded, onToggle }) {
  const { t } = useTranslation('attorney');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusClass = (app) => {
    // Check for special conditions first
    if (app.hasAdverseReport) return 'status-warning';
    if (app.hasTask) return 'status-attention';

    switch (app.status) {
      case 'registered':
        return 'status-success';
      case 'filed':
        return 'status-filed';
      case 'pending_attorney_filing':
      case 'filing_in_progress':
        return 'status-pending';
      case 'payment_complete':
        return 'status-ready';
      case 'active':
      case 'draft':
        return 'status-draft';
      default:
        return 'status-draft';
    }
  };

  const getStatusLabel = (app) => {
    // Show adverse report status if applicable
    if (app.hasAdverseReport) {
      return 'Adverse Report';
    }

    switch (app.status) {
      case 'registered': return 'Registered';
      case 'filed': return 'Filed';
      case 'pending_attorney_filing': return 'Pending Filing';
      case 'filing_in_progress': return 'Filing in Progress';
      case 'payment_complete': return 'Ready to File';
      case 'active':
        // Check if it's actually a draft (not submitted for payment)
        return 'Draft';
      case 'draft': return 'Draft';
      default: return app.status || 'Draft';
    }
  };

  return (
    <div className={`client-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="client-header" onClick={onToggle}>
        <div className="client-avatar">
          {client.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="client-info">
          <h3 className="client-name">{client.name || 'Unknown Client'}</h3>
          <span className="client-email">{client.email || 'No email'}</span>
        </div>
        <div className="client-summary">
          <span className="summary-item">
            <strong>{client.totalApplications}</strong> {t('attorney.clients.applications', 'applications')}
          </span>
          {client.pendingFilings > 0 && (
            <span className="summary-item pending">
              <strong>{client.pendingFilings}</strong> {t('attorney.clients.pendingFilings', 'pending filings')}
            </span>
          )}
        </div>
        <div className="client-expand">
          {isExpanded ? '▼' : '▶'}
        </div>
      </div>

      {isExpanded && (
        <div className="client-details">
          <div className="client-meta">
            <span className="meta-item">
              <span className="meta-label">{t('attorney.clients.lastActivity', 'Last Activity')}:</span>
              <span className="meta-value">{formatDate(client.lastActivity)}</span>
            </span>
            <span className="meta-item">
              <span className="meta-label">{t('attorney.clients.activeTrademarks', 'Active Trademarks')}:</span>
              <span className="meta-value">{client.activeTrademarks}</span>
            </span>
          </div>

          <h4>{t('attorney.clients.applicationsTitle', 'Applications')}</h4>
          <div className="applications-list">
            {client.applications.map((app) => (
              <div key={app.id} className="application-card">
                <div className="application-row">
                  <div className="app-info">
                    <span className="app-name">{app.trademarkName}</span>
                    {app.businessName && (
                      <span className="app-business">{app.businessName}</span>
                    )}
                    {app.applicationId && (
                      <span className="app-id">{app.applicationId}</span>
                    )}
                  </div>
                  <span className={`app-status ${getStatusClass(app)}`}>
                    {getStatusLabel(app)}
                  </span>
                  {/* Assignment info */}
                  {app.assignment?.isTransferred && (
                    <span className="app-assignment transferred">
                      {app.assignment.orgName || 'External Firm'}
                    </span>
                  )}
                  {app.assignment?.attorneyName && !app.assignment?.isTransferred && (
                    <span className="app-assignment assigned">
                      {app.assignment.attorneyName}
                    </span>
                  )}
                  <div className="app-filings">
                    {app.filings?.map((filing) => (
                      <span
                        key={filing.jurisdiction}
                        className={`filing-badge ${filing.status}`}
                        title={`${filing.jurisdiction}: ${filing.status}`}
                      >
                        {filing.jurisdiction}
                      </span>
                    ))}
                  </div>
                  <span className="app-date">{formatDate(app.createdAt)}</span>
                  {/* Only show View link for submitted applications (not drafts) */}
                  {app.status && !['draft', 'active'].includes(app.status) && (
                    <a
                      href={`/attorney/filing-queue?app=${app.id}`}
                      className="app-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('attorney.clients.view', 'View')} &rarr;
                    </a>
                  )}
                </div>

                {/* Linked Items Details */}
                {app.linkedItems && (
                  <>
                    {/* Correspondence */}
                    {app.linkedItems.correspondence?.length > 0 && (
                      <div className="linked-section">
                        <div className="linked-section-header correspondence">
                          <span className="linked-icon">&#x2709;</span>
                          Correspondence ({app.linkedItems.correspondence.length})
                        </div>
                        <div className="linked-list">
                          {app.linkedItems.correspondence.map(c => (
                            <a
                              key={c.id}
                              href={`/attorney/correspondence?id=${c.id}`}
                              className="linked-list-item linked-list-item-clickable"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="linked-item-title">{c.subject || 'No subject'}</span>
                              <span className={`linked-item-badge ${c.classification}`}>
                                {c.subType || c.classification || 'General'}
                              </span>
                              {c.jurisdiction && (
                                <span className="linked-item-jurisdiction">{c.jurisdiction}</span>
                              )}
                              <span className="linked-item-date">{formatDate(c.receivedAt)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Deadlines */}
                    {app.linkedItems.deadlines?.length > 0 && (
                      <div className="linked-section">
                        <div className="linked-section-header deadlines">
                          <span className="linked-icon">&#x23F0;</span>
                          Deadlines ({app.linkedItems.deadlines.length})
                        </div>
                        <div className="linked-list">
                          {app.linkedItems.deadlines.map(d => (
                            <a
                              key={d.id}
                              href={`/attorney/deadlines?id=${d.id}`}
                              className="linked-list-item linked-list-item-clickable"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="linked-item-title">{d.title}</span>
                              <span className={`linked-item-badge urgency-${d.urgency}`}>
                                {d.urgency}
                              </span>
                              <span className="linked-item-date">Due: {formatDate(d.dueDate)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tasks */}
                    {app.linkedItems.tasks?.length > 0 && (
                      <div className="linked-section">
                        <div className="linked-section-header tasks">
                          <span className="linked-icon">&#x2713;</span>
                          Tasks ({app.linkedItems.tasks.length})
                        </div>
                        <div className="linked-list">
                          {app.linkedItems.tasks.map(task => (
                            <a
                              key={task.id}
                              href={`/attorney/approval-queue?task=${task.id}`}
                              className="linked-list-item linked-list-item-clickable"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="linked-item-title">{task.summary || task.agent}</span>
                              <span className={`linked-item-badge priority-${task.priority}`}>
                                {task.priority}
                              </span>
                              <span className={`linked-item-badge status-${task.status}`}>
                                {task.status?.replace(/_/g, ' ')}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
